/**
 * Base API Service
 * 
 * This class provides common functionality for all API services,
 * reducing duplication and providing a consistent interface.
 */

import apiClient from './apiClient';

export class BaseApiService {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }

    // ============================================================================
    // GENERIC CRUD OPERATIONS
    // ============================================================================

    /**
     * Generic list operation with pagination and filtering
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async list(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params });
            const responseData = response.data;

            // Robustly handle the modern SuccessResponse envelope: 
            // { success, message, data: { items: [...], meta: {...} } }
            if (responseData?.success && responseData?.data && typeof responseData.data === 'object') {
                let itemsArray = null;
                let meta = responseData.data.meta || {};

                if (Array.isArray(responseData.data.items)) {
                    itemsArray = responseData.data.items;
                } else if (responseData.data.items && Array.isArray(responseData.data.items.users)) {
                    // Handle shape: items = { users:[...], total_count, ... }
                    const { users, ...rest } = responseData.data.items;
                    itemsArray = users;
                    // Prefer meta if present, otherwise use rest as meta-like object
                    if (Object.keys(meta).length === 0) meta = rest;
                } else if (Array.isArray(responseData.data.users)) {
                    itemsArray = responseData.data.users;
                }

                if (itemsArray) {
                    // Preserve nested structure for pagination support
                    // This maintains the original backend format: {data: {items: [...], meta: {...}}}
                    return {
                        success: true,
                        data: {
                            items: itemsArray,
                            meta: meta
                        },
                        message: responseData.message,
                        // Also provide flattened structure for backward compatibility
                        items: itemsArray,
                        ...meta
                    };
                }
                
                // If it's a success envelope but not the items/meta structure, return it as is.
                // This handles single-resource GETs that use the same wrapper.
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message
                };
            }

            // Legacy envelope without `success` (direct list or paginated object)
            if (responseData && typeof responseData === 'object' && !('success' in responseData)) {
                const dataKey = Object.keys(responseData).find(key => Array.isArray(responseData[key]));
                if (dataKey) {
                    const pageData = (({ users, ...o }) => o)(responseData);
                    return {
                        success: true,
                        data: responseData[dataKey],
                        ...pageData
                    };
                }
            }
            
            // Fallback for raw data response or unexpected structures
            return {
                success: true,
                data: responseData
            };

        } catch (error) {
            console.error(`Error listing ${this.baseUrl}:`, error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || `Failed to retrieve ${this.baseUrl}`
            };
        }
    }

    /**
     * Generic get by ID operation
     * @param {string} id - Resource ID
     * @returns {Promise<Object>} - API response
     */
    async getById(id) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${id}`);
            const responseData = response.data;
            
            // Handle the standard API response format: { success, data, message }
            if (responseData && typeof responseData === 'object') {
                if (responseData.success && responseData.data) {
                    return {
                        success: true,
                        data: responseData.data,
                        message: responseData.message || "Resource fetched successfully"
                    };
                } else if (responseData.success === false) {
                    return {
                        success: false,
                        data: null,
                        message: responseData.message || "Failed to fetch resource"
                    };
                }
            }
            
            // Fallback for direct data response
            return {
                success: true,
                data: responseData,
                message: "Resource fetched successfully"
            };
        } catch (error) {
            console.error(`Error fetching ${this.baseUrl}/${id}:`, error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Generic create operation
     * @param {Object} data - Resource data
     * @returns {Promise<Object>} - API response
     */
    async create(data) {
        try {
            const response = await apiClient.post(this.baseUrl, data);
            return {
                success: true,
                data: response.data,
                message: "Resource created successfully"
            };
        } catch (error) {
            console.error(`Error creating ${this.baseUrl}:`, error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Generic update operation
     * @param {string} id - Resource ID
     * @param {Object} data - Updated resource data
     * @returns {Promise<Object>} - API response
     */
    async update(id, data) {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
            return {
                success: true,
                data: response.data,
                message: "Resource updated successfully"
            };
        } catch (error) {
            console.error(`Error updating ${this.baseUrl}/${id}:`, error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Generic delete operation
     * @param {string} id - Resource ID
     * @returns {Promise<Object>} - API response
     */
    async delete(id) {
        try {
            await apiClient.delete(`${this.baseUrl}/${id}`);
            return {
                success: true,
                data: null,
                message: "Resource deleted successfully"
            };
        } catch (error) {
            console.error(`Error deleting ${this.baseUrl}/${id}:`, error);
            throw this.handleApiError(error);
        }
    }

    // ============================================================================
    // COMMON OPERATIONS
    // ============================================================================

    /**
     * Generic search operation
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} - API response
     */
    async search(searchParams) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/search`, { params: searchParams });
            const responseData = response.data;

            if (responseData && typeof responseData === 'object' && !('success' in responseData)) {
                const dataKey = Object.keys(responseData).find(key => Array.isArray(responseData[key]));
                if (dataKey) {
                    const pageData = (({ users, ...o }) => o)(responseData) 
                    return {
                        success: true,
                        data: responseData[dataKey],
                        ...pageData
                    };
                }
            }
            return responseData;
        } catch (error) {
            console.error(`Error searching ${this.baseUrl}:`, error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || "Search failed"
            };
        }
    }

    /**
     * Generic bulk operation
     * @param {string} operation - Operation type (delete, update, etc.)
     * @param {Object} data - Bulk operation data
     * @returns {Promise<Object>} - API response
     */
    async bulk(operation, data) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/bulk/${operation}`, data);
            return {
                success: true,
                data: response.data,
                message: `Bulk ${operation} completed successfully`
            };
        } catch (error) {
            console.error(`Error in bulk ${operation} for ${this.baseUrl}:`, error);
            throw this.handleApiError(error);
        }
    }

    /**
     * Generic status update operation
     * @param {string} id - Resource ID
     * @param {string} status - New status
     * @returns {Promise<Object>} - API response
     */
    async updateStatus(id, status) {
        try {
            const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, { status });
            return {
                success: true,
                data: response.data,
                message: "Status updated successfully"
            };
        } catch (error) {
            console.error(`Error updating status for ${this.baseUrl}/${id}:`, error);
            throw this.handleApiError(error);
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Standardized error handling
     * @param {Object} error - API error
     * @returns {Object} - Formatted error response
     */
    handleApiError(error) {
        return {
            success: false,
            data: null,
            status: error.response?.status || 0,
            message: error.response?.data?.detail || error.message || 'An error occurred'
        };
    }

    /**
     * Validate required fields
     * @param {Object} data - Data to validate
     * @param {Array} requiredFields - Array of required field names
     * @throws {Error} - If required fields are missing
     */
    validateRequiredFields(data, requiredFields) {
        const missingFields = requiredFields.filter(field => !data[field]);
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
    }

    /**
     * Convert legacy field names to modern API format
     * @param {Object} data - Data with legacy field names
     * @param {Object} fieldMap - Mapping of legacy to modern field names
     * @returns {Object} - Data with modern field names
     */
    transformLegacyFields(data, fieldMap) {
        const transformed = {};
        
        for (const [modernField, legacyField] of Object.entries(fieldMap)) {
            if (data[legacyField] !== undefined) {
                transformed[modernField] = data[legacyField];
            } else if (data[modernField] !== undefined) {
                transformed[modernField] = data[modernField];
            }
        }
        
        // Include any fields not in the mapping
        for (const [key, value] of Object.entries(data)) {
            if (!Object.values(fieldMap).includes(key) && !fieldMap[key]) {
                transformed[key] = value;
            }
        }
        
        return transformed;
    }

    /**
     * Remove undefined values from object
     * @param {Object} obj - Object to clean
     * @returns {Object} - Cleaned object
     */
    removeUndefinedValues(obj) {
        const cleaned = {};
        Object.keys(obj).forEach(key => {
            if (obj[key] !== undefined) {
                cleaned[key] = obj[key];
            }
        });
        return cleaned;
    }

    /**
     * Create a standardized success response
     * @param {*} data - Response data
     * @param {string} message - Success message
     * @returns {Object} - Standardized response
     */
    createSuccessResponse(data, message = "Operation completed successfully") {
        return {
            success: true,
            data,
            message
        };
    }

    /**
     * Create a standardized error response
     * @param {string} message - Error message
     * @param {*} data - Error data
     * @returns {Object} - Standardized error response
     */
    createErrorResponse(message, data = null) {
        return {
            success: false,
            data,
            message
        };
    }

    // ============================================================================
    // PERFORMANCE OPTIMIZATIONS
    // ============================================================================

    /**
     * Get lightweight version of resources
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - Lightweight API response
     */
    async listLightweight(params = {}) {
        return this.list({ ...params, lightweight: true });
    }

    /**
     * Get resources with specific fields only
     * @param {Object} params - Query parameters
     * @param {Array} fields - Fields to include
     * @returns {Promise<Object>} - Filtered API response
     */
    async listWithFields(params = {}, fields = []) {
        return this.list({ 
            ...params, 
            fields: fields.join(',') 
        });
    }

    // ============================================================================
    // BATCH OPERATIONS
    // ============================================================================

    /**
     * Get multiple resources by IDs in a single request
     * @param {Array} ids - Array of resource IDs
     * @returns {Promise<Object>} - Batch API response
     */
    async getBatch(ids) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/batch`, { ids });
            return {
                success: true,
                data: response.data,
                message: "Batch fetch completed successfully"
            };
        } catch (error) {
            console.error(`Error in batch fetch for ${this.baseUrl}:`, error);
            throw this.handleApiError(error);
        }
    }
}

export default BaseApiService; 